// Third-party
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router';
import { LocationDescriptorObject } from 'history';

// Selectors
import { selectSavedFundsShelfStatusedFund } from '@/redux/modules/shelf-management/selectors';
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { getSavedFunds, toggleSavedFund } from '@/redux/modules/funds/saved-funds';
import { addFundsToComparisonById, ADD_FUNDS_TO_COMPARISON } from '@/redux/modules/comparison';
import { addFundsToPortfolio, ADD_FUNDS_TO_PORTFOLIO, resetPortfolio } from '@/redux/modules/edit-portfolio';

// Columns Definition
import { getColumnsDefinition, SearchResultColumns } from '@/views/components/search/columns-definition/SearchResult';

// Components
import AdvancedSearchResult from '@/views/components/search/AdvancedSearchResult';

// Enums
import { SearchType } from '@/enums/AdvancedSearch';

// Types
import Fund from '@/types/fund';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { searchObjectsOnFields } from '@/utils/search';
import { NAV_PATHS } from '@/utils/constants';

// Legacy support
import withLegacyVersion from '@/views/components/hocs/withLegacyVersion';
import LegacySavedFunds from './LegacySavedFunds';

type DispatchProps = {
  getSavedFunds: Function;
  toggleSavedFund: Function;
  addFundsToComparisonById: (ids: string[]) => Promise<void[]>;
  addFundsToPortfolio: Function;
  resetPortfolio: Function;
};

type StateProps = {
  funds: Fund[];
  loading: boolean;
  addingToPortfolio: boolean;
  addingToComparison: boolean;
  user?: Nullable<User>;
};

type Props = RouteComponentProps &
  DispatchProps &
  StateProps & {
    query?: string;
    locale: Locale;
  };

interface State {
  selected: Fund[];
  redirect?: string | LocationDescriptorObject;
}

class SavedFunds extends React.Component<Props, State> {
  readonly state: State = {
    selected: [],
  };

  readonly tableColumns = [
    SearchResultColumns.INLINE_ADD,
    SearchResultColumns.NAME,
    SearchResultColumns.UNIVERSE,
    SearchResultColumns.CLASSIFICATION,
    SearchResultColumns.ONE_YEAR,
    SearchResultColumns.THREE_YEARS,
    SearchResultColumns.FIVE_YEARS,
    SearchResultColumns.INC,
    SearchResultColumns.MER,
    SearchResultColumns.BETA,
    SearchResultColumns.STD_DEV,
    SearchResultColumns.REMOVE_FROM_FAVORITES,
    SearchResultColumns.IS_FOCUSED,
  ];

  onAddToPortfolio = () => {
    this.props.resetPortfolio();
    this.props.addFundsToPortfolio(this.state.selected).then(() => {
      this.setState({ redirect: '/models?newPortfolio=true' });
    });
  };

  onAddToComparison = () => {
    const { location } = this.props;
    const ids = this.state.selected.map(f => f._id);
    this.props.addFundsToComparisonById(ids).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({
        redirect: {
          pathname: NAV_PATHS.COMPARISON,
          state: { from: location },
        },
      });
    });
  };

  getActions = () => {
    const { addingToPortfolio, addingToComparison } = this.props;
    return [
      {
        loading: addingToPortfolio,
        icon: 'plus',
        label: <FormattedMessage id={`Search.Funds.${addingToPortfolio ? 'AddingToPortfolio' : 'AddToPortfolio'}`} />,
        onClick: this.onAddToPortfolio,
      },
      {
        isLoading: addingToComparison,
        icon: 'plus',
        label: <FormattedMessage id={`Search.Funds.${addingToComparison ? 'AddingToComparison' : 'AddToComparison'}`} />,
        onClick: this.onAddToComparison,
      },
    ];
  };

  handleRowSelectionChange = (selected: Fund[]) => {
    this.setState({ selected });
  };

  getFilteredFunds = () => {
    const { query, funds } = this.props;

    if (!query) {
      return funds;
    }

    return searchObjectsOnFields(query, funds, ['names.short', 'names.full', 'symbols', 'classification', 'universe']);
  };

  getDisclaimers = () => [formatMessage('Disclaimers.NET_PERFORMANCE'), formatMessage('Disclaimers.CompareFundsForMoreInfo')];

  toggleSaved = (row: Fund) => {
    const fund = this.props.funds.find(f => f._id === row._id);
    if (!fund) {
      return;
    }

    this.props.toggleSavedFund(fund);

    this.setState(prevState => ({
      selected: prevState.selected.filter(f => f !== row),
    }));
  };

  redirectToSearch = () => this.setState({ redirect: '/search/funds' });

  render() {
    const { loading, locale, query, user } = this.props;
    const { selected, redirect } = this.state;

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    const { columns } = getColumnsDefinition({
      onToggleSaved: this.toggleSaved,
      allowedColumns: this.tableColumns,
      type: SearchType.FUNDS,
      user,
      locale,
    });

    return (
      <AdvancedSearchResult
        actions={this.getActions()}
        canSelect
        className="saved-funds"
        columns={columns}
        data={this.getFilteredFunds()}
        itemType={SearchType.FUNDS}
        loading={loading}
        searchQuery={query}
        selectedFunds={selected}
        selectedTitle="Search.Funds.SelectedTitle"
        onRowSelectionChange={this.handleRowSelectionChange}
        showNoDataComponent
      />
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  funds: selectSavedFundsShelfStatusedFund(state.funds.savedFunds).funds,
  loading: state.funds.savedFunds.loading,
  locale: state.intl.locale,
  addingToPortfolio: selectIsLoadingEvery(state, [ADD_FUNDS_TO_PORTFOLIO.type]),
  addingToComparison: selectIsLoadingEvery(state, [ADD_FUNDS_TO_COMPARISON.type]),
  user: state.auth.user,
});

export default withLegacyVersion(
  connect(mapStateToProps, {
    getSavedFunds,
    toggleSavedFund,
    addFundsToComparisonById,
    addFundsToPortfolio,
    resetPortfolio,
  })(SavedFunds),
  LegacySavedFunds,
);
